import {
  Ban,
  CalendarDays,
  MessageCircleMore,
  MessageSquareText,
  PackageCheck,
  RefreshCcw,
  Star,
  Store,
  Truck,
  UtensilsCrossed,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Order } from "../../../../Redux/custmoer_orders/types";
import {
  formatFoodCurrency,
  formatFoodOrderDate,
  getFoodOrderItemCount,
  mapOrderToFoodOrderRecord,
} from "../ordersAdapters";

interface FoodOrderItemsProps {
  orders: Order[];
  onTrack: (order: Order) => void;
  onCancel: (order: Order) => void;
  onChat: (order: Order) => void;
}

const FoodOrderItems: React.FC<FoodOrderItemsProps> = ({
  orders,
  onTrack,
  onCancel,
  onChat,
}) => {
  const navigate = useNavigate();

  if (!orders || orders.length === 0) {
    return (
      <div className="food-orders-empty">
        <UtensilsCrossed size={34} strokeWidth={1.8} />
        <strong>No food orders found</strong>
        <span>Try another search or date range.</span>
      </div>
    );
  }

  return (
    <>
      {orders.map((rawOrder, idx) => {
        const order = mapOrderToFoodOrderRecord(rawOrder);
        const rawOrderId = rawOrder.orderId || (rawOrder as any).id;
        const firstProductId = rawOrder.productDetails?.[0]?.productId;

        return (
          <article className="food-order-card" key={rawOrderId || idx}>
            <div className="food-order-card-top">
              <div className="food-order-status-wrap">
                <span
                  className={`food-order-status-pill status-${order.stage}`}
                >
                  <PackageCheck size={15} strokeWidth={2} />
                  {order.stageLabel}
                </span>
                <div className="food-order-status-copy">
                  <strong>
                    {order.stage === "delivered"
                      ? `Delivered ${
                          order.deliveredAt
                            ? formatFoodOrderDate(order.deliveredAt)
                            : ""
                        }`
                      : order.stageLabel}
                  </strong>
                  <p>{order.statusMessage}</p>
                </div>
              </div>

              <div className="food-order-meta">
                <span>ORDER ID</span>
                <strong>#{order.id}</strong>
                <small>
                  <CalendarDays size={13} strokeWidth={2} />
                  {formatFoodOrderDate(order.orderedAt)}{" "}
                  {order.orderedTime ? `· ${order.orderedTime}` : ""}
                </small>
              </div>
            </div>

            <div className="food-order-card-body">
              <div className="food-order-content">
                <div className="food-order-restaurant">
                  <div className="food-order-restaurant-image">
                    <img
                      src={order.restaurantImage}
                      alt={order.restaurantName}
                      loading="lazy"
                    />
                  </div>
                  <div className="food-order-restaurant-copy">
                    <span className="food-order-label">RESTAURANT</span>
                    <h2>{order.restaurantName}</h2>
                    <p>{order.restaurantArea || "Location"}</p>
                    <button
                      type="button"
                      className="food-order-view-details"
                      onClick={() => onTrack(rawOrder)}
                    >
                      View details
                    </button>
                  </div>
                </div>

                <div className="food-order-items">
                  <div className="food-order-items-head">
                    <span className="food-order-label">
                      {getFoodOrderItemCount(order)} ITEMS
                    </span>
                    <strong>
                      {formatFoodCurrency(order.bill.grandTotal)}
                    </strong>
                  </div>
                  <div className="food-order-item-list">
                    {order.items.map((item) => (
                      <div className="food-order-item-row" key={item.id}>
                        <span
                          className={`food-order-food-mark ${
                            item.isVeg ? "veg" : "non-veg"
                          }`}
                          aria-label={
                            item.isVeg ? "Vegetarian" : "Non vegetarian"
                          }
                        >
                          <i />
                        </span>
                        <span>
                          {item.quantity}× {item.name}
                        </span>
                        <strong>
                          {formatFoodCurrency(item.price * item.quantity)}
                        </strong>
                      </div>
                    ))}
                  </div>
                  <div className="food-order-total-row">
                    <div>
                      <span>Bill total</span>
                      <strong>
                        {formatFoodCurrency(order.bill.grandTotal)}
                      </strong>
                    </div>
                    <button
                      type="button"
                      className="food-order-reorder"
                      disabled
                    >
                      <RefreshCcw size={16} strokeWidth={2} />
                      Reorder
                    </button>
                  </div>
                </div>
              </div>

              <aside className="food-order-actions">
                <button
                  type="button"
                  className="primary"
                  disabled={order.stage === "cancelled"}
                  onClick={() => onTrack(rawOrder)}
                >
                  <span className="food-order-action-icon">
                    <Truck size={17} strokeWidth={2} />
                  </span>
                  <b>Track Order</b>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/FoodSellerFeeback/${
                        rawOrder.sellerDetails?.sellerId || rawOrderId
                      }${rawOrderId ? `?orderId=${rawOrderId}` : ""}`,
                    )
                  }
                >
                  <span className="food-order-action-icon">
                    <Store size={17} strokeWidth={2} />
                  </span>
                  <b>Restaurant Feedback</b>
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/FoodDeliveryFeedback")}
                >
                  <span className="food-order-action-icon">
                    <MessageSquareText size={17} strokeWidth={2} />
                  </span>
                  <b>Delivery Feedback</b>
                </button>
                {firstProductId && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/FoodProductFeedback/${firstProductId}`)
                    }
                  >
                    <span className="food-order-action-icon">
                      <Star size={17} strokeWidth={2} />
                    </span>
                    <b>Write Food Review</b>
                  </button>
                )}
                <button
                  type="button"
                  className="danger"
                  disabled={order.stage === "cancelled"}
                  onClick={() => onCancel(rawOrder)}
                >
                  <span className="food-order-action-icon">
                    <Ban size={17} strokeWidth={2} />
                  </span>
                  <b>
                    {order.stage === "cancelled"
                      ? "Order Cancelled"
                      : "Cancel Order"}
                  </b>
                </button>
                <button type="button" onClick={() => onChat(rawOrder)}>
                  <span className="food-order-action-icon">
                    <MessageCircleMore size={17} strokeWidth={2} />
                  </span>
                  <b>Chat with us</b>
                </button>
              </aside>
            </div>
          </article>
        );
      })}
    </>
  );
};

export default FoodOrderItems;
